'use client'

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  Beaker,
  Send,
  Loader2,
  CheckCircle2,
  User,
  Mail,
  Phone,
  Building,
  MessageSquare,
  Clock,
  Package,
  FileText
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import { siloApi } from '@/lib/api/silo-api'

// Zod schema validation as specified in PRP
export const chemistInquirySchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Phone number is required (at least 10 digits)'),
  company: z.string().min(2, 'Company name required'),
  projectType: z.enum(['beverage', 'patch', 'topical', 'vape', 'gummy', 'other']),
  projectDescription: z.string().min(20, 'Please provide project details (min 20 chars)'),
  timeline: z.enum(['immediate', '1-3months', '3-6months', '6months+']),
  volume: z.string().optional(),
  additionalInfo: z.string().optional(),
})

export type ChemistInquiryData = z.infer<typeof chemistInquirySchema>

interface ChemistContactFormProps {
  onSubmitSuccess?: () => void
}

export function ChemistContactForm({ onSubmitSuccess }: ChemistContactFormProps) {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const form = useForm<ChemistInquiryData>({
    resolver: zodResolver(chemistInquirySchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      projectType: 'beverage',
      projectDescription: '',
      timeline: 'immediate',
      volume: '',
      additionalInfo: '',
    },
  })

  // Submit form with metadata for backend routing
  const onSubmit = async (data: ChemistInquiryData) => {
    setIsSubmitting(true)

    try {
      // Format the request text with all project details
      const requestText = `
        CHEMIST INQUIRY - ${data.projectType.toUpperCase()}
        
        Company: ${data.company}
        Timeline: ${data.timeline}
        
        Project Description:
        ${data.projectDescription}
        
        ${data.volume ? `Expected Volume: ${data.volume}` : ''}
        ${data.additionalInfo ? `Additional Information:\n${data.additionalInfo}` : ''}
      `.trim()

      // Prepare the submission payload with CRITICAL metadata for backend routing
      const submissionPayload = {
        name: data.name,
        email: data.email,
        phone: data.phone, // Optional field
        company: data.company, // CRITICAL: Send company at root level for business creation
        request: requestText,
        
        // CRITICAL: Metadata for backend routing - inquiry_type: 'chemist_question'
        metadata: {
          // Primary routing field - backend looks for this
          inquiry_type: 'chemist_question',
          
          // Form identification
          form_type: 'ask_a_chemist',
          source: 'chemist_modal',
          
          // Business context for proper routing (keep in metadata too for backward compatibility)
          company: data.company,
          project_type: data.projectType,
          timeline: data.timeline,
          
          // Optional enrichment data
          volume: data.volume || null,
          submission_context: 'hero_section',
          created_at: new Date().toISOString(),
          
          // User context
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          page_url: typeof window !== 'undefined' ? window.location.href : null,
          referrer: typeof document !== 'undefined' ? document.referrer : null
        }
      }

      console.log('Submitting chemist inquiry:', submissionPayload)

      // Submit to existing API with metadata
      const response = await siloApi.submitPublicContactForm(submissionPayload)
      
      console.log('Chemist inquiry response:', response)

      setSubmitSuccess(true)
      form.reset()

      toast({
        title: 'Inquiry Submitted Successfully!',
        description: 'Our chemistry team will review your project and contact you within 24 hours.',
      })

      // Call success callback
      onSubmitSuccess?.()

    } catch (error: any) {
      console.error('Failed to submit chemist inquiry:', error)
      
      toast({
        title: 'Submission Failed',
        description: error.message || 'Failed to submit your inquiry. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Success screen
  if (submitSuccess) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4 p-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <CheckCircle2 className="h-16 w-16 text-green-400 mx-auto" />
          </motion.div>
          <div>
            <h3 className="text-xl font-semibold text-hierarchy-1 mb-2">
              Chemist Inquiry Submitted!
            </h3>
            <p className="text-hierarchy-3">
              Thank you for your interest in our formulation services. Our chemistry team will review your project requirements and contact you within 24 hours.
            </p>
          </div>
          <Button 
            onClick={() => setSubmitSuccess(false)}
            className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black font-semibold"
          >
            Submit Another Inquiry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="mb-6">
        <p className="text-sm text-hierarchy-3">
          Tell us about your formulation needs and our chemistry team will get back to you with expert guidance.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 flex-1">
          {/* Name Field */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Full Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter your full name"
                    className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Email Field */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="Enter your email address"
                    className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Phone Field (Required) */}
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    placeholder="Enter your phone number"
                    required
                    className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Company Field */}
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Company Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter your company name"
                    className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Project Type Dropdown */}
          <FormField
            control={form.control}
            name="projectType"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <Beaker className="h-4 w-4" />
                  Project Type
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-gray-800/30 border-white/10 text-white">
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-gray-800 border-white/10">
                    <SelectItem value="beverage" className="text-white hover:bg-white/10">Beverage Formulation</SelectItem>
                    <SelectItem value="patch" className="text-white hover:bg-white/10">Patch Formulation</SelectItem>
                    <SelectItem value="topical" className="text-white hover:bg-white/10">Topical Formulation</SelectItem>
                    <SelectItem value="vape" className="text-white hover:bg-white/10">Vape Formulation</SelectItem>
                    <SelectItem value="gummy" className="text-white hover:bg-white/10">Gummy Formulation</SelectItem>
                    <SelectItem value="other" className="text-white hover:bg-white/10">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Project Description */}
          <FormField
            control={form.control}
            name="projectDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Project Description
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Tell us about your project requirements, desired effects, target demographics, etc..."
                    className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40 min-h-[100px] resize-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Timeline Dropdown */}
          <FormField
            control={form.control}
            name="timeline"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Project Timeline
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-gray-800/30 border-white/10 text-white">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-gray-800 border-white/10">
                    <SelectItem value="immediate" className="text-white hover:bg-white/10">Immediate (ASAP)</SelectItem>
                    <SelectItem value="1-3months" className="text-white hover:bg-white/10">1-3 Months</SelectItem>
                    <SelectItem value="3-6months" className="text-white hover:bg-white/10">3-6 Months</SelectItem>
                    <SelectItem value="6months+" className="text-white hover:bg-white/10">6+ Months</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Volume Field (Optional) */}
          <FormField
            control={form.control}
            name="volume"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Expected Volume (Optional)
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g., 1000 units, 10kg, small batch"
                    className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Additional Info (Optional) */}
          <FormField
            control={form.control}
            name="additionalInfo"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/80 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Additional Information (Optional)
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Any additional details, regulatory requirements, or questions..."
                    className="bg-gray-800/30 border-white/10 text-white placeholder:text-white/40 min-h-[80px] resize-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-semibold py-3 mt-6"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting Inquiry...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                Submit Chemist Inquiry
              </div>
            )}
          </Button>
        </form>
      </Form>
    </div>
  )
}